import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../../lib/LanguageContext";
import { PRODUCT_CARDS } from "../../lib/brand";
import { REGISTER_URL } from "../../lib/config";
import { trackRegisterClick } from "../../lib/analytics";
import UrgencyBadge from "./UrgencyBadge";
import { Button } from "../ui/button";
import { ArrowRight, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Products() {
  const { lang, t } = useLang();
  const [items, setItems] = useState(PRODUCT_CARDS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/products`)
      .then((res) => {
        if (mounted && Array.isArray(res.data) && res.data.length > 0) setItems(res.data);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const pickName = (p) => (lang === "id" ? p.name_id || p.nameId || p.name : p.name);

  return (
    <section className="py-20 sm:py-28" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-red">{t.products.eyebrow}</span>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink leading-tight" data-testid="products-title">
              {t.products.title}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-ink-muted">{t.products.subtitle}</p>
          </div>
          <a
            href={REGISTER_URL}
            className="shrink-0"
            rel="noopener"
            onClick={() => trackRegisterClick("products-unlock")}
          >
            <Button variant="outline" className="rounded-full border-2 border-ink/10 hover:border-brand-red hover:text-brand-red h-12 px-6 font-semibold" data-testid="products-unlock-btn">
              <Lock className="w-4 h-4 mr-2" />
              {t.products.unlock}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="product-grid">
          {items.map((p, idx) => (
            <div
              key={p.id || idx}
              className={`group rounded-2xl bg-white border border-soft overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300 ${loading ? "opacity-90" : ""}`}
              data-testid={`product-card-${idx + 1}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#F3F3F3]">
                <img
                  src={p.img}
                  alt={pickName(p)}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white text-[10px] font-bold uppercase tracking-wider text-brand-red">
                  {t.products[p.tag]}
                </div>
                <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-brand-red text-white text-xs font-bold">
                  {p.margin}
                </div>
              </div>
              <div className="p-4 sm:p-5">
                <h3 className="font-semibold text-ink text-sm sm:text-base line-clamp-1">{pickName(p)}</h3>

                <div className="mt-2 min-h-[24px]">
                  <UrgencyBadge id={p.id || `seed-${idx}`} lang={lang} />
                </div>

                <div className="mt-3 flex items-baseline justify-between">
                  <div>
                    <div className="text-[10px] font-semibold uppercase text-ink-muted">Wholesale</div>
                    <div className="font-display font-bold text-ink">{p.wholesale}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-semibold uppercase text-ink-muted">Retail</div>
                    <div className="font-display font-bold text-brand-orange">{p.retail}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
